import type { ExtinctionLawName, SpectralState } from "../../data/types";
import { FaderRow } from "./FaderRow";

interface Props {
  /** Controls are live only in the dust (reddening) view. */
  active: boolean;
  av: number;
  onAvChange: (value: number) => void;
  law: ExtinctionLawName;
  onLawChange: (law: ExtinctionLawName) => void;
  /** Null when the object has no state to redden; the picker is then empty. */
  templateState: SpectralState | null;
  onTemplateStateChange: (state: SpectralState) => void;
  /**
   * States the loaded object actually has, each labelled with the year of its
   * first epoch in that state, so the picker is built from the data.
   */
  templateOptions: Array<{ state: SpectralState; label: string }>;
  /** Object-specific reddening benchmarks from its paper, if any. */
  benchmarks: string | null;
}

export function DustSection(props: Props) {
  const { active, av, onAvChange, law, onLawChange, templateState, onTemplateStateChange, templateOptions, benchmarks } = props;
  return (
    <div className="panel-section">
      <h2>Dust / reddening</h2>
      <p className="muted" style={{ fontSize: 11 }}>
        {active
          ? "Apply an extinction law to a brighter template and compare with the current epoch."
          : "Switch to the Dust mode (top right) to enable the reddening overlay."}
      </p>
      <div className="dust-row">
        <span>Law</span>
        <select value={law} onChange={(e) => onLawChange(e.target.value as ExtinctionLawName)} disabled={!active}>
          <option value="maiolino_agn">Maiolino AGN</option>
          <option value="mw_rv44">MW (RV=4.4)</option>
          <option value="lmc">LMC</option>
        </select>
      </div>
      <div className="dust-row">
        <span>Template</span>
        <select
          value={templateState ?? ""}
          onChange={(e) => onTemplateStateChange(e.target.value as SpectralState)}
          disabled={!active}
        >
          {templateOptions.map((opt) => (
            <option key={opt.state} value={opt.state}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      <FaderRow label="A_V" max={2.5} value={av} onChange={onAvChange} readout={av.toFixed(2)} disabled={!active} />
      {benchmarks ? (
        <p className="muted" style={{ fontSize: 11, marginTop: 4 }}>
          {benchmarks}
        </p>
      ) : null}
    </div>
  );
}
